const KEY_PREFIX = "progsity:playground:draft:";

/** Skip drafting pathological payloads — backend caps source at 50KB anyway. */
const DRAFT_SOURCE_MAX = 60_000;
const DRAFT_STDIN_MAX = 10_000;

export type PlaygroundDraft = {
  sourceCode: string;
  stdin: string;
  savedAt: number;
};

function keyFor(languageId: string): string {
  return `${KEY_PREFIX}${languageId}`;
}

/**
 * Unsaved scratch work, per language — restored on the next visit so closing
 * the tab never loses code. Saved snippets are excluded (they persist via API).
 */
export function loadPlaygroundDraft(languageId: string): PlaygroundDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(keyFor(languageId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const sourceCode = parsed["sourceCode"];
    if (typeof sourceCode !== "string" || sourceCode.length === 0) return null;
    return {
      sourceCode,
      stdin: typeof parsed["stdin"] === "string" ? parsed["stdin"] : "",
      savedAt: typeof parsed["savedAt"] === "number" ? parsed["savedAt"] : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Returns whether the draft is now on this device. `false` covers the two
 * cases a student should hear about (`PG-PWA01`): storage refused the write
 * (quota, a private mode that blocks it) and a source too large to draft. A
 * silent `false` used to be the only outcome, so nobody knew their draft was
 * not being kept.
 */
export function savePlaygroundDraft(
  languageId: string,
  draft: { sourceCode: string; stdin: string },
): boolean {
  if (draft.sourceCode.length > DRAFT_SOURCE_MAX) return false;
  try {
    window.localStorage.setItem(
      keyFor(languageId),
      JSON.stringify({
        sourceCode: draft.sourceCode,
        stdin: draft.stdin.slice(0, DRAFT_STDIN_MAX),
        savedAt: Date.now(),
      }),
    );
    return true;
  } catch {
    /* quota / private mode */
    return false;
  }
}

export function clearPlaygroundDraft(languageId: string): void {
  try {
    window.localStorage.removeItem(keyFor(languageId));
  } catch {
    /* ignore */
  }
}
